import { useCallback, useEffect, useRef, useState } from 'react'
import type { ChatTransport, SSEFrame, StreamBody } from './transport'
import type {
  ChatMessage,
  ClarifyQuestion,
  DownloadInfo,
  LoadedMessage,
  LoadedThread,
  ThinkingEntry,
} from './types'

// Small coercion helpers — SSE payloads are `unknown`.
const str = (v: unknown): string => (typeof v === 'string' ? v : v == null ? '' : String(v))
const strArr = (v: unknown): string[] => (Array.isArray(v) ? v.map(str) : [])

interface PersistedChat {
  threadId: string | null
  messages: ChatMessage[]
  pendingQuestion: ClarifyQuestion | null
  // Identity (tenant+user) that produced this transcript. Stamped on save and
  // verified on load so a blob left in shared localStorage by one login is never
  // adopted by another on a single-origin app — the thread_id especially, which
  // the backend rejects with 403 ("does not belong to client …") when it carries
  // a different tenant's thread. Absent for unscoped callers (the widget).
  owner?: string | null
}

function loadPersisted(key: string, owner?: string): PersistedChat | null {
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return null
    const p = JSON.parse(raw) as PersistedChat
    if (!Array.isArray(p?.messages)) return null
    // Owner-scoped callers (the dashboard passes the signed-in tenant) may only
    // rehydrate their OWN persisted chat. A missing or different stamp means the
    // blob is a prior login's — refuse it rather than resume its thread_id.
    if (owner !== undefined && (p.owner ?? null) !== owner) return null
    return {
      threadId: p.threadId ?? null,
      messages: p.messages,
      pendingQuestion: p.pendingQuestion ?? null,
      owner: p.owner ?? null,
    }
  } catch {
    return null
  }
}

function savePersisted(key: string, p: PersistedChat): void {
  try {
    localStorage.setItem(key, JSON.stringify(p))
  } catch {
    // storage full/unavailable — non-fatal; the chat just won't survive refresh.
  }
}

function clearPersisted(key: string): void {
  try {
    localStorage.removeItem(key)
  } catch {
    // ignore
  }
}

// Highest assistant-bubble id in a (restored) transcript. turnSeq must start
// ABOVE this on mount: restored bubbles keep the ids they were persisted with,
// but the counter otherwise restarts at 0 — so the first new turn would reuse
// id 1, collide with the first restored reply, and patch-by-id (findIndex hits
// the OLD bubble first) would paint the answer onto it, leaving the new bubble
// blank. That's the "reply doesn't render after a refresh / navigating back"
// bug. loadThread already keeps ids ahead; this covers the initial restore.
function maxAssistantId(messages: ChatMessage[] | undefined): number {
  let mx = 0
  for (const m of messages ?? []) {
    if (m.role === 'assistant' && m.id > mx) mx = m.id
  }
  return mx
}

// Token pacing. The network (and the SSE proxy) deliver tokens in bursts; if we
// render each one the instant it lands the text appears in lumpy spurts. Instead
// we buffer incoming tokens and drain them on a steady requestAnimationFrame
// cadence: at least PACE_MIN chars/frame for a smooth typewriter trickle, but
// `ceil(backlog / PACE_DIV)` when behind so a big burst catches up within a few
// frames instead of lagging seconds behind the model. This also collapses many
// per-token React updates into at most one per frame.
const PACE_MIN = 3
const PACE_DIV = 6

export interface UseChatOptions {
  /** HOW to open the stream — auth/headers/refresh live in here (per host). */
  stream: ChatTransport
  /** Persist transcript + thread under this localStorage key so a refresh
   *  restores the conversation. Omit for an ephemeral chat. */
  persistKey?: string
  /** Fetch a past conversation by id (history). Required for `loadThread(id)`
   *  to fetch; if a caller passes messages to loadThread directly, unused. */
  loadThreadMessages?: (id: string) => Promise<LoadedThread>
  /** Token pacing (rAF typewriter). Default true; pass false to append instantly. */
  paced?: boolean
  /** Identity that owns the persisted chat (e.g. `clientId:username`). When set,
   *  the transcript+thread are stamped with it and only rehydrated for the same
   *  owner, and changing it tears the current chat down — so a single-origin app
   *  that switches logins in place can never send the prior tenant's thread_id.
   *  Omit for an unscoped chat (the widget runs inside one authenticated host). */
  owner?: string
}

export interface UseChat {
  messages: ChatMessage[]
  isLoading: boolean
  pendingQuestion: ClarifyQuestion | null
  threadId: string | null
  /** Send a user message. `opts.incidentId` marks the turn as a structured
   *  incident investigation — the backend routes it deterministically into the
   *  alert-investigation flow instead of classifying the text. */
  send: (text: string, opts?: { incidentId?: string }) => void
  sendResume: (value: string, label?: string) => void
  /** Interrupt the in-flight turn: abort the stream, keep whatever has already
   *  streamed into the bubble, and hand control back to the user. No-op when
   *  nothing is in flight. */
  stop: () => void
  startNewChat: () => void
  /** Replace the current transcript with a past conversation and resume it.
   *  Pass `messages` to load them directly (host already fetched them), or omit
   *  to fetch via `loadThreadMessages`. Rejects (leaving the current chat
   *  untouched) if a fetch fails. */
  loadThread: (id: string, messages?: LoadedMessage[]) => Promise<void>
}

export function useChat({ stream, persistKey, loadThreadMessages, paced = true, owner }: UseChatOptions): UseChat {
  // Rehydrate any persisted chat so a refresh restores the transcript — but only
  // the current owner's (see loadPersisted). Lazy `useState(initializer)` runs
  // the read exactly once.
  const [restored] = useState(() => (persistKey ? loadPersisted(persistKey, owner) : null))
  const [messages, setMessages] = useState<ChatMessage[]>(() => restored?.messages ?? [])
  const [isLoading, setIsLoading] = useState(false)
  const [pendingQuestion, setPendingQuestion] = useState<ClarifyQuestion | null>(
    () => restored?.pendingQuestion ?? null,
  )
  const [threadId, setThreadId] = useState<string | null>(() => restored?.threadId ?? null)
  const threadIdRef = useRef<string | null>(restored?.threadId ?? null)
  const abortRef = useRef<AbortController | null>(null)
  const turnSeq = useRef(maxAssistantId(restored?.messages))
  // Generation token. Bumped whenever a turn starts and whenever we tear a turn
  // down (load a past thread / new chat). Streaming callbacks capture the
  // generation they were created under and bail if it's since changed, so an
  // aborted stream still draining can't write onto a conversation the user has
  // since switched away from.
  const genRef = useRef(0)
  // Handle for the in-flight token-pacing animation frame, kept at hook scope so
  // a new chat / unmount can cancel a drain that's mid-flight.
  const rafRef = useRef<number | null>(null)
  // Latest `owner`, read by the persist effect so it always stamps the current
  // identity without `owner` being one of its deps (which would let it re-save
  // the prior identity's transcript before the owner-change effect resets it).
  const ownerRef = useRef(owner)
  ownerRef.current = owner
  // Previous `owner`, to detect an in-place identity switch (below).
  const prevOwnerRef = useRef(owner)

  // Cancel any pending drain frame on unmount.
  useEffect(
    () => () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current)
    },
    [],
  )

  // Persist the transcript + thread id so a refresh restores the conversation.
  // Only write once the turn has settled (isLoading false); writing on every
  // streamed frame would thrash localStorage. An empty, thread-less state clears
  // the store so "New chat" doesn't leave a stale blob.
  useEffect(() => {
    if (!persistKey) return
    if (isLoading) return
    if (messages.length === 0 && threadId === null) {
      clearPersisted(persistKey)
      return
    }
    savePersisted(persistKey, { threadId, messages, pendingQuestion, owner: ownerRef.current ?? null })
  }, [persistKey, messages, threadId, pendingQuestion, isLoading])

  // The signed-in identity changed while this hook stayed mounted — an in-place
  // login that didn't remount us (single-origin app, switching companies in the
  // same tab). The in-memory threadId/transcript still belong to the PREVIOUS
  // tenant; without this they'd be sent under the new token and the backend would
  // 403 ("thread does not belong to client …"). Tear the old turn down and adopt
  // the new owner's persisted chat (normally none — login wiped it), so the next
  // message opens a fresh thread for the new tenant.
  useEffect(() => {
    if (prevOwnerRef.current === owner) return
    prevOwnerRef.current = owner
    genRef.current += 1
    abortRef.current?.abort()
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current)
      rafRef.current = null
    }
    const next = persistKey ? loadPersisted(persistKey, owner) : null
    threadIdRef.current = next?.threadId ?? null
    turnSeq.current = maxAssistantId(next?.messages)
    setThreadId(next?.threadId ?? null)
    setMessages(next?.messages ?? [])
    setPendingQuestion(next?.pendingQuestion ?? null)
    setIsLoading(false)
  }, [owner, persistKey])

  const runStream = useCallback(
    async (body: StreamBody) => {
      setIsLoading(true)
      setPendingQuestion(null)

      const controller = new AbortController()
      abortRef.current = controller

      // Per-turn state. The assistant bubble is tagged with `turnId` and always
      // patched BY ID, so an interleaved question/download can never misdirect a
      // token append to the wrong message. `gen` tags the whole turn so callbacks
      // from a torn-down stream stop touching state (see genRef).
      const gen = ++genRef.current
      const turnId = ++turnSeq.current
      const thinking: ThinkingEntry[] = []
      let reasoning = ''
      let started = false

      const ensureBubble = () => {
        if (started) return
        started = true
        setMessages((prev) => [
          ...prev,
          { role: 'assistant', id: turnId, content: '', thinking: [...thinking], reasoning },
        ])
      }
      type AssistantMessage = Extract<ChatMessage, { role: 'assistant' }>
      // `patch` may be a function when the new value depends on the current
      // bubble (appending a download to the ones already offered this turn).
      const patchAssistant = (
        patch:
          | Partial<AssistantMessage>
          | ((current: AssistantMessage) => Partial<AssistantMessage>),
      ) =>
        setMessages((prev) => {
          const i = prev.findIndex((m) => m.role === 'assistant' && m.id === turnId)
          if (i < 0) return prev
          const current = prev[i] as AssistantMessage
          const next = [...prev]
          next[i] = {
            ...current,
            ...(typeof patch === 'function' ? patch(current) : patch),
          } as ChatMessage
          return next
        })
      // Local mirror of the bubble's content (kept in sync by every mutation
      // below) plus the committed prefix: once the backend signals
      // `addendum_start`, the audit has accepted the streamed answer and the
      // agent is APPENDING an update — resets must preserve the committed part
      // instead of blanking the bubble.
      let live = ''
      let committed = ''
      const appendContent = (chunk: string) => {
        live += chunk
        setMessages((prev) => {
          const i = prev.findIndex((m) => m.role === 'assistant' && m.id === turnId)
          if (i < 0) return prev
          const cur = prev[i]
          if (cur.role !== 'assistant') return prev
          const next = [...prev]
          next[i] = { ...cur, content: cur.content + chunk }
          return next
        })
      }

      // --- token pacing: buffer tokens, drain on a rAF cadence (see PACE_*) -----
      let pending = ''
      const pump = () => {
        rafRef.current = null
        if (!pending) return
        const n = Math.max(PACE_MIN, Math.ceil(pending.length / PACE_DIV))
        const slice = pending.slice(0, n)
        pending = pending.slice(n)
        appendContent(slice)
        if (pending) schedule()
      }
      const schedule = () => {
        if (rafRef.current == null) rafRef.current = requestAnimationFrame(pump)
      }
      const enqueueToken = (chunk: string) => {
        ensureBubble()
        if (paced) {
          pending += chunk
          schedule()
        } else {
          appendContent(chunk)
        }
      }
      const stopPump = () => {
        if (rafRef.current != null) {
          cancelAnimationFrame(rafRef.current)
          rafRef.current = null
        }
      }
      // Flush whatever is buffered immediately (stream end / no `final` event).
      const flushNow = () => {
        stopPump()
        if (pending) {
          appendContent(pending)
          pending = ''
        }
      }
      // A new answer pass is starting (the agent called a tool, or a quality
      // rerun discarded the last draft). Drop the buffered + rendered draft so
      // the bubble shows only the current answer — never a pile of stale
      // drafts. An answer committed by `addendum_start` survives the reset.
      const resetAnswer = () => {
        pending = ''
        live = committed
        if (started) patchAssistant({ content: committed })
      }

      const pushThinking = (entry: ThinkingEntry) => {
        thinking.push(entry)
        if (started) patchAssistant({ thinking: [...thinking] })
      }

      const onEvent = ({ event, data }: SSEFrame) => {
        // A newer turn started, or the user loaded a different thread / hit "new
        // chat" — this stream is stale, so drop its events instead of writing
        // them onto whatever conversation is on screen now.
        if (gen !== genRef.current) return
        const d = (data ?? {}) as Record<string, unknown>
        switch (event) {
          case 'thread':
            if (d.thread_id) {
              threadIdRef.current = str(d.thread_id)
              setThreadId(threadIdRef.current)
            }
            break
          case 'reasoning_token':
            if (d.content) {
              reasoning += str(d.content)
              ensureBubble()
              patchAssistant({ reasoning })
            }
            break
          case 'token':
            if (d.content) enqueueToken(str(d.content))
            break
          case 'answer_reset':
            // Backend signal that the prior streamed draft is being discarded
            // (a quality rerun is writing a fresh answer). Clear the bubble.
            // Kept for back-compat: current flows send addendum_start instead.
            resetAnswer()
            break
          case 'addendum_start':
            // The completeness audit kept the streamed answer and the agent is
            // appending an update below it: freeze what's rendered (plus any
            // buffered tail) as committed, add a separator, and let the
            // addendum stream in after it. Tool events during the addendum
            // pass reset only the uncommitted part.
            flushNow()
            if (live) {
              ensureBubble()
              committed = `${live}\n\n---\n\n`
              live = committed
              patchAssistant({ content: committed })
            }
            break
          case 'final':
            // Authoritative final answer. Stop the pacer and set it outright.
            if (d.message) {
              stopPump()
              pending = ''
              live = str(d.message)
              ensureBubble()
              patchAssistant({ content: live, thinking: [...thinking] })
            }
            break
          case 'question': {
            const q = data as ClarifyQuestion
            setMessages((prev) => [...prev, { role: 'question', question: q }])
            setPendingQuestion(q)
            break
          }
          case 'download': {
            // Store the raw payload so the shared DownloadCard reads whatever
            // fields the backend sends (filename/url/row_count/expires/…).
            // APPEND: one turn can offer several files (an SLA answer sends the
            // PDF and the CSV), and a single slot silently kept only the last.
            ensureBubble()
            patchAssistant((prev) => {
              const next = [...(prev.downloads ?? []), d as DownloadInfo]
              return { downloads: next, download: next[0] }
            })
            break
          }
          case 'reasoning_decision':
            pushThinking({
              kind: 'reasoning_decision',
              needs_reasoning: Boolean(d.needs_reasoning),
              complexity: str(d.complexity),
              why: str(d.why),
            })
            break
          case 'route':
            pushThinking({ kind: 'route', flow: str(d.flow), reasoning: str(d.reasoning) })
            break
          case 'memory_load':
            pushThinking({ kind: 'memory_load', client: strArr(d.client), skills: strArr(d.skills) })
            break
          case 'memory_write':
            pushThinking({ kind: 'memory_write', slug: str(d.slug), type: d.type ? str(d.type) : undefined })
            break
          case 'skill_save':
            pushThinking({ kind: 'skill_save', slug: str(d.slug) })
            break
          case 'tool':
            // The agent is calling a tool, so anything it streamed just now was
            // pre-tool narration ("let me check…"), not the answer. Move on:
            // record the tool in the trace and clear the draft from the bubble.
            pushThinking({ kind: 'tool', name: str(d.name), args: d.args })
            resetAnswer()
            break
          case 'tool_result':
            pushThinking({
              kind: 'tool_result',
              name: str(d.name),
              summary: d.summary ? str(d.summary) : undefined,
              error: d.error ? str(d.error) : undefined,
            })
            break
          case 'node':
            if (d.name && d.phase === 'complete') pushThinking({ kind: 'node', name: str(d.name) })
            break
          case 'error':
            throw new Error(str(d.detail) || 'stream error')
          // 'done' and any unknown events: ignore
        }
      }

      try {
        await stream(body, onEvent, controller.signal)
        // Stream ended cleanly: render any tokens still buffered by the pacer
        // (covers flows that stream the answer without a trailing `final`).
        flushNow()
      } catch (err) {
        // Torn down (new chat / thread switch / owner change bump the gen
        // BEFORE aborting): don't touch the pump or the transcript — the raf
        // handle and the messages may already belong to a newer turn.
        if (gen !== genRef.current) return
        if ((err as { name?: string } | null)?.name === 'AbortError') {
          // The user hit Stop: keep whatever already streamed (including the
          // pacer's buffered tail), then mark the turn as interrupted so the
          // transcript shows the reply was cut short, not finished.
          flushNow()
          ensureBubble()
          patchAssistant({ content: live ? `${live}\n\n*Interrupted*` : '*Interrupted*' })
          return
        }
        stopPump()
        pending = ''
        const msg = err instanceof Error ? err.message : 'Something went wrong.'
        ensureBubble()
        patchAssistant({ content: `⚠️ ${msg}` })
      } finally {
        // Guarded like the catch: a stale turn's cleanup must not clobber a
        // newer turn's spinner or its live AbortController.
        if (gen === genRef.current) setIsLoading(false)
        if (abortRef.current === controller) abortRef.current = null
      }
    },
    [stream, paced],
  )

  const send = useCallback(
    (text: string, opts?: { incidentId?: string }) => {
      const t = text.trim()
      if (!t || isLoading || pendingQuestion) return
      setMessages((prev) => [...prev, { role: 'user', content: t }])
      void runStream({
        message: t,
        thread_id: threadIdRef.current ?? undefined,
        incident_id: opts?.incidentId,
      })
    },
    [isLoading, pendingQuestion, runStream],
  )

  const sendResume = useCallback(
    (value: string, label?: string) => {
      // Mirror send's guard: don't resume if a turn is in flight or no question
      // is actually pending (defends against a double-click race).
      if (isLoading || !pendingQuestion || !threadIdRef.current) return
      setMessages((prev) => [...prev, { role: 'user', content: label ?? value }])
      void runStream({ thread_id: threadIdRef.current, resume_value: value })
    },
    [isLoading, pendingQuestion, runStream],
  )

  const stop = useCallback(() => {
    abortRef.current?.abort()
  }, [])

  const startNewChat = useCallback(() => {
    genRef.current += 1
    abortRef.current?.abort()
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current)
      rafRef.current = null
    }
    threadIdRef.current = null
    setThreadId(null)
    setMessages([])
    setPendingQuestion(null)
    setIsLoading(false)
  }, [])

  const loadThread = useCallback(
    async (id: string, messages?: LoadedMessage[]) => {
      // Resolve the messages BEFORE touching any state: a failed fetch
      // (404/expired/offline) must leave the current conversation exactly as it
      // was, so the caller can surface an error without losing what's on screen.
      let loadedId = id
      let loadedMsgs: LoadedMessage[]
      if (messages) {
        loadedMsgs = messages
      } else if (loadThreadMessages) {
        const detail = await loadThreadMessages(id)
        loadedId = detail.id
        loadedMsgs = detail.messages
      } else {
        throw new Error('loadThread: pass messages, or configure loadThreadMessages')
      }

      // Committed to the switch — invalidate the old turn's callbacks, then tear
      // down anything in flight from it.
      genRef.current += 1
      abortRef.current?.abort()
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }

      // Rebuild the transcript. Assistant bubbles need a fresh turn id (kept
      // ahead of turnSeq so the next live turn can't collide) and the
      // streaming-only fields (thinking/reasoning) which history omits.
      const loaded: ChatMessage[] = loadedMsgs.map((m) =>
        m.role === 'assistant'
          ? { role: 'assistant', id: ++turnSeq.current, content: m.content, thinking: [], reasoning: '' }
          : { role: 'user', content: m.content },
      )

      threadIdRef.current = loadedId
      setThreadId(loadedId)
      setMessages(loaded)
      setPendingQuestion(null)
      setIsLoading(false)
    },
    [loadThreadMessages],
  )

  return {
    messages,
    isLoading,
    pendingQuestion,
    threadId,
    send,
    sendResume,
    stop,
    startNewChat,
    loadThread,
  }
}
